<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { HelpCircle, PlayCircle } from 'lucide-vue-next';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import useSharedStore from '@/presentation/store/shared';

const sharedStore = useSharedStore();
const route = useRoute();
const router = useRouter();
const NAVIGATION_TOUR_COUNT_KEY = 'newty.businessTour.navigationShownCount';
const AUTO_TOUR_SHOWN_KEY_PREFIX = 'newty.businessTour.autoShown.';
const AUTO_START_ROUTE_NAMES = new Set([
	'Index',
	'DashboardSetting',
	'DashboardRequests',
	'DashboardReceiveRequests',
	'DashboardSendRequests',
	'BookAppointment',
	'DashboardCustomerClub',
]);

const isBusinessUser = computed(() => sharedStore.userInformation?.role === 'Business');
let activeTour: ReturnType<typeof driver> | null = null;
let autoTourTimeout: number | undefined;
let processedDirectTour = '';

const getNavigationTourCount = () => {
	if (typeof window === 'undefined') return 0;
	const value = Number.parseInt(window.localStorage.getItem(NAVIGATION_TOUR_COUNT_KEY) || '0', 10);
	return Number.isFinite(value) ? value : 0;
};

const setNavigationTourCount = (value: number) => {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(NAVIGATION_TOUR_COUNT_KEY, String(Math.max(0, value)));
};

const incrementNavigationTourCount = () => {
	setNavigationTourCount(getNavigationTourCount() + 1);
};

const getRouteName = () => String(route.name || '');

const getAutoTourStorageKey = (routeName: string) => `${AUTO_TOUR_SHOWN_KEY_PREFIX}${routeName}`;

const hasAutoTourShown = (routeName: string) => {
	if (typeof window === 'undefined') return true;
	return window.localStorage.getItem(getAutoTourStorageKey(routeName)) === '1';
};

const markAutoTourShown = (routeName: string) => {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(getAutoTourStorageKey(routeName), '1');
};

const shouldAutoStartRoute = (_routeName: string) => false;

const findVisibleElement = (selector: string) =>
	Array.from(document.querySelectorAll<HTMLElement>(selector)).find((element) => {
		const style = window.getComputedStyle(element);
		return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
	});

const withElement = (selector: string, popover: NonNullable<DriveStep['popover']>): DriveStep => ({
	element: () => findVisibleElement(selector) as Element,
	popover,
});

const resolveElement = (step: DriveStep) => {
	if (!step.element) return true;
	if (typeof step.element === 'string') return Boolean(findVisibleElement(step.element));
	if (typeof step.element === 'function') return Boolean(step.element());
	return step.element.getClientRects().length > 0;
};

const getStepTourTarget = (step: DriveStep) => {
	const stepElement = step.element;
	if (!stepElement || typeof stepElement === 'string') return '';
	const element = typeof stepElement === 'function' ? stepElement() : stepElement;
	return element?.getAttribute('data-business-tour') || '';
};

const scrollTargetIntoView = (element?: Element) => {
	if (!(element instanceof HTMLElement)) return;

	element.scrollIntoView({
		block: 'center',
		inline: 'nearest',
		behavior: 'auto',
	});
};

const refreshAfterScroll: NonNullable<DriveStep['onHighlighted']> = (_element, _step, opts) => {
	window.requestAnimationFrame(() => {
		opts.driver.refresh();
		window.setTimeout(() => opts.driver.refresh(), 120);
	});
};

const toPersianDigits = (value: string) => value.replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);

const renderPopoverProgress = (popover: any, opts: any) => {
	const current = Number(opts.state.activeIndex ?? 0) + 1;
	const total = opts.config.steps?.length ?? current;
	popover.progress.textContent = `${toPersianDigits(String(current))} از ${toPersianDigits(String(total))}`;
};

const handleHighlightStarted: NonNullable<DriveStep['onHighlightStarted']> = (element, _step, opts) => {
	scrollTargetIntoView(element);
	window.requestAnimationFrame(() => opts.driver.refresh());
};

const getNavigationSteps = (): DriveStep[] => [
	withElement('[data-business-tour="nav-Index"]', {
		title: 'داشبورد',
		description: 'خلاصه وضعیت کسب و کار، موجودی، اعتبار پیامکی، نوبت‌های نزدیک و آمار مشتریان را یکجا می‌بینید.',
		side: 'left',
		align: 'center',
	}),
	withElement('[data-business-tour="nav-BookAppointment"]', {
		title: 'نوبت دهی',
		description: 'برای ثبت نوبت دستی، دیدن درخواست‌های دریافتی و پیگیری رزروهای کسب و کار از این بخش استفاده کنید.',
		side: 'left',
		align: 'center',
	}),
	withElement('[data-business-tour="nav-DashboardCustomerClub"]', {
		title: 'باشگاه مشتریان',
		description: 'تحلیل مشتریان وفادار، مشتریان در آستانه ریزش، منابع بازگشت، کمپین‌ها و اقدام‌های نگهداشت از این تب دنبال می‌شود.',
		side: 'left',
		align: 'center',
	}),
	withElement('[data-business-tour="nav-ProfileShopId"]', {
		title: 'پروفایل کسب و کار',
		description: 'نمای بیرونی کسب و کار برای مشتریان اینجاست؛ اطلاعات، تصاویر، خدمات و ظاهر صفحه را بررسی کنید.',
		side: 'left',
		align: 'center',
	}),
	withElement('[data-business-tour="nav-DashboardSetting"]', {
		title: 'تنظیمات',
		description: 'اطلاعات هویتی، دسته بندی، خدمات، متخصصان، اتصال خدمات و پیامک‌ها از این بخش تنظیم می‌شوند.',
		side: 'left',
		align: 'center',
	}),
];

const getDashboardSteps = (): DriveStep[] => [
	withElement('[data-business-tour="dashboard-header"]', {
		title: 'شروع سریع از داشبورد',
		description: 'این صفحه برای تصمیم‌های روزانه است؛ اگر چیزی ناقص باشد، هشدارهای راه اندازی همین بالا نمایش داده می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setup-notice"]', {
		title: 'وضعیت آماده سازی رزرو آنلاین',
		description: 'اگر سرویس، متخصص، دسته بندی یا اتصال خدمات کامل نباشد، این کارت قدم بعدی را پیشنهاد می‌دهد.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="wallet-card"]', {
		title: 'کیف پول',
		description: 'موجودی کسب و کار، شارژ حساب و برداشت از این کارت در دسترس است.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="financial-management-card"]', {
		title: 'مدیریت مالی',
		description: 'گزارش‌های مالی، تراکنش‌ها و برداشت‌های کسب و کار از این کارت باز می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="sms-credit-card"]', {
		title: 'اعتبار پیامکی',
		description: 'تعداد پیامک‌های باقی مانده را می‌بینید و در صورت نیاز اعتبار پیامکی را شارژ می‌کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="quick-actions"]', {
		title: 'اقدام سریع',
		description: 'کارهای پرتکرار مثل افزودن خدمت، متخصص، مشتری، تنظیم ساعت کاری و ثبت نوبت دستی از اینجا سریع‌تر انجام می‌شود.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="appointments"]', {
		title: 'نوبت‌های نزدیک',
		description: 'نوبت‌های امروز یا نوبت‌های پیش رو را می‌بینید و برای بررسی جزئیات وارد هر رزرو می‌شوید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="customers"]', {
		title: 'مشتریان',
		description: 'تعداد مشتریان جدید، مشتریان VIP و کل مشتریان برای پایش رشد کسب و کار نمایش داده می‌شود.',
		side: 'top',
		align: 'center',
	}),
];

const getSettingSteps = (): DriveStep[] => [
	withElement('[data-business-tour="setting-profile-card"]', {
		title: 'کارت کسب و کار',
		description: 'از این کارت می‌توانید ظاهر عمومی صفحه کسب و کار را ببینید و وضعیت فعال بودن کسب و کار را بررسی کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-artists-link"]', {
		title: 'افزودن و مدیریت متخصص',
		description: 'متخصصان یا اعضای تیم را اینجا تعریف می‌کنید. برای رزرو آنلاین، حداقل یک متخصص فعال لازم است.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-services-link"]', {
		title: 'تعریف خدمات',
		description: 'خدمات قابل ارائه، قیمت و مدت زمان هر خدمت از این بخش ثبت می‌شود تا مشتری بتواند آن را انتخاب کند.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-assignments-link"]', {
		title: 'اتصال خدمات به متخصص',
		description: 'بعد از تعریف خدمت و متخصص، مشخص کنید هر خدمت توسط کدام متخصص انجام می‌شود. بدون این اتصال، خدمت قابل رزرو نیست.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-send-sms-link"]', {
		title: 'ارسال پیامک',
		description: 'برای اطلاع‌رسانی، یادآوری یا پیام تبلیغاتی به مشتری‌ها وارد این بخش شوید و گیرنده، متن و زمان ارسال را مشخص کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-online-reservation"]', {
		title: 'نمایش دکمه رزرو وقت',
		description: 'با روشن بودن این گزینه، دکمه رزرو آنلاین در پروفایل کسب و کار نمایش داده می‌شود.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="setting-reserve-payment"]', {
		title: 'پرداخت از طریق درگاه',
		description: 'اگر این گزینه روشن باشد، مشتری هنگام رزرو می‌تواند پرداخت آنلاین را از طریق درگاه انجام دهد.',
		side: 'top',
		align: 'center',
	}),
];

const getArtistsSteps = (): DriveStep[] => [
	withElement('[data-business-tour="artists-header"]', {
		title: 'مدیریت متخصصان',
		description: 'در این صفحه فهرست متخصصان کسب و کار، وضعیت فعال بودن و تعداد خدمات هر متخصص را مدیریت می‌کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="artist-create-button"]', {
		title: 'افزودن متخصص',
		description: 'برای اضافه کردن متخصص جدید این دکمه را بزنید. بعد از ثبت اطلاعات، مرحله اتصال خدمات نمایش داده می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="artist-list"]', {
		title: 'لیست متخصصان',
		description: 'هر کارت، اطلاعات یک متخصص را نشان می‌دهد؛ از اینجا وضعیت، تجربه و تعداد خدمات متصل را بررسی کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="artist-actions"]', {
		title: 'ویرایش متخصص',
		description: 'از منوی هر متخصص می‌توانید اطلاعات او را ویرایش کنید، خدماتش را تغییر دهید یا در صورت نیاز حذفش کنید.',
		side: 'left',
		align: 'center',
	}),
];

const getCreateArtistSteps = (): DriveStep[] => [
	withElement('[data-business-tour="create-artist-form"]', {
		title: 'اطلاعات متخصص',
		description: 'نام، عکس، توضیحات و ساعت کاری متخصص را کامل کنید تا در مرحله بعد خدمات قابل انجام او مشخص شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="create-artist-submit"]', {
		title: 'ثبت و اتصال خدمات',
		description: 'بعد از ایجاد متخصص، جدول اتصال خدمات باز می‌شود تا مشخص کنید این متخصص چه خدماتی انجام می‌دهد.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="assignment-panel"]', {
		title: 'اتصال خدمات به متخصص',
		description: 'اگر متخصص ثبت شده باشد، از این جدول خدمات قابل انجام او را انتخاب و ذخیره کنید.',
		side: 'top',
		align: 'center',
	}),
];

const getServicesSteps = (): DriveStep[] => [
	withElement('[data-business-tour="services-create-button"]', {
		title: 'تعریف خدمت جدید',
		description: 'برای ثبت خدمت، قیمت و مدت زمان انجام آن از این دکمه استفاده کنید. بعد از ثبت، اتصال متخصص انجام می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="services-summary"]', {
		title: 'وضعیت خدمات',
		description: 'این آمار نشان می‌دهد چند خدمت تعریف شده، چند مورد قابل رزرو است و کدام خدمات هنوز متخصص ندارند.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="services-list"]', {
		title: 'لیست خدمات',
		description: 'هر خدمت با قیمت، مدت زمان و وضعیت قابل رزرو بودن نمایش داده می‌شود. خدمت بدون متخصص برای مشتری قابل رزرو نیست.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="service-assign-action"]', {
		title: 'اتصال متخصص به خدمت',
		description: 'از این گزینه متخصصانی را انتخاب کنید که این خدمت را انجام می‌دهند تا رزرو آنلاین آن فعال شود.',
		side: 'top',
		align: 'center',
	}),
];

const getAssignmentsSteps = (): DriveStep[] => [
	withElement('[data-business-tour="assignment-panel"]', {
		title: 'اتصال خدمات به متخصصان',
		description: 'این جدول مشخص می‌کند هر خدمت توسط کدام متخصص انجام می‌شود. ستون‌ها متخصصان و ردیف‌ها خدمات هستند.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="assignment-matrix"]', {
		title: 'انتخاب اتصال‌ها',
		description: 'تیک هر خانه یعنی آن متخصص می‌تواند آن خدمت را انجام دهد. برای رزرو آنلاین، هر خدمت باید حداقل یک متخصص فعال داشته باشد.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="assignment-save"]', {
		title: 'ذخیره اتصال‌ها',
		description: 'بعد از تغییر انتخاب‌ها، ذخیره را بزنید تا وضعیت قابل رزرو بودن خدمات به‌روزرسانی شود.',
		side: 'top',
		align: 'center',
	}),
];

const getSendSmsSteps = (): DriveStep[] => [
	withElement('[data-business-tour="send-sms-credit"]', {
		title: 'اعتبار پیامکی',
		description: 'قبل از ارسال، تعداد پیامک‌های باقی‌مانده را بررسی کنید و اگر کافی نیست اعتبار را شارژ کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="send-sms-recipient"]', {
		title: 'انتخاب گیرنده',
		description: 'می‌توانید یک مشتری، چند مشتری انتخابی یا یک گروه مثل همه مشتریان یا مشتریان VIP را انتخاب کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="send-sms-message"]', {
		title: 'متن پیامک',
		description: 'متن پیام را بنویسید. شمارنده تعداد کاراکتر و تعداد پیامک مصرفی را نشان می‌دهد.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="send-sms-ready-messages"]', {
		title: 'متن‌های آماده',
		description: 'برای ارسال سریع‌تر، یکی از متن‌های آماده را انتخاب کنید و در صورت نیاز آن را ویرایش کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="send-sms-time"]', {
		title: 'زمان ارسال',
		description: 'ارسال را برای همین حالا یا یک زمان مشخص در آینده تنظیم کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="send-sms-submit"]', {
		title: 'پیش‌نمایش و ارسال',
		description: 'قبل از ارسال نهایی، گیرنده، متن، زمان و هزینه پیامک را در پیش‌نمایش بررسی کنید.',
		side: 'top',
		align: 'center',
	}),
];

const getWalletSteps = (): DriveStep[] => [
	withElement('[data-business-tour="wallet-balance"]', {
		title: 'موجودی کیف پول',
		description: 'موجودی فعلی کسب و کار، آخرین شارژ و تعداد تراکنش‌های این ماه در این بخش نمایش داده می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="wallet-charge"]', {
		title: 'شارژ حساب',
		description: 'برای افزایش موجودی، مبلغ شارژ را وارد کنید و وارد مسیر پرداخت شوید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="wallet-transactions"]', {
		title: 'تراکنش‌ها',
		description: 'شارژها، برداشت‌ها و پرداخت‌های رزرو در این جدول یا کارت‌ها قابل پیگیری هستند.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="wallet-withdraw"]', {
		title: 'برداشت از حساب',
		description: 'برای تسویه، مبلغ برداشت را وارد کنید. درخواست برداشت بعد از ثبت در انتظار بررسی قرار می‌گیرد.',
		side: 'top',
		align: 'center',
	}),
];

const getFinancialSteps = (): DriveStep[] => [
	withElement('[data-business-tour="financial-header"]', {
		title: 'صفحه مالی',
		description: 'در این صفحه درآمد، پرداخت‌های رزرو، تراکنش‌ها و برداشت‌های کسب و کار را یکجا بررسی می‌کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="financial-metrics"]', {
		title: 'شاخص‌های مالی',
		description: 'کارت‌ها وضعیت درآمد، میانگین رزرو، تعداد رزروها و درخواست‌های برداشت را نشان می‌دهند.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="financial-chart"]', {
		title: 'نمودار درآمد',
		description: 'روند درآمد ماه جاری را اینجا می‌بینید و می‌توانید جزئیات بیشتری از درآمد را بررسی کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="financial-withdraw-status"]', {
		title: 'وضعیت برداشت',
		description: 'آخرین درخواست برداشت و وضعیت تسویه از این کارت قابل پیگیری است.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="financial-details"]', {
		title: 'جزئیات مالی',
		description: 'با انتخاب تراکنش، رزرو یا برداشت، جزئیات آن در پنل کنار صفحه نمایش داده می‌شود.',
		side: 'top',
		align: 'center',
	}),
];

const getRequestsHomeSteps = (): DriveStep[] => [
	withElement('[data-business-tour="requests-home-header"]', {
		title: 'مدیریت رزروها',
		description: 'از این صفحه وارد ثبت نوبت دستی، درخواست‌های دریافتی مشتریان و درخواست‌های ارسالی خودتان می‌شوید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-book-link"]', {
		title: 'ثبت رزرو',
		description: 'برای ثبت نوبت دستی داخل سالن یا ثبت نوبت از طرف مشتری، این مسیر را انتخاب کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-receive-link"]', {
		title: 'درخواست‌های دریافتی',
		description: 'درخواست‌هایی که مشتریان از پروفایل شما ثبت کرده‌اند اینجا دیده می‌شود و می‌توانید آن‌ها را تایید یا رد کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-send-link"]', {
		title: 'درخواست‌های ارسال شده',
		description: 'نوبت‌هایی که شما ثبت یا ارسال کرده‌اید از این بخش پیگیری می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
];

const getReceiveRequestsSteps = (): DriveStep[] => [
	withElement('[data-business-tour="request-status-tabs"]', {
		title: 'تب‌های وضعیت رزرو',
		description: 'در انتظار تایید برای درخواست‌های جدید، پیش رو برای نوبت‌های تاییدشده آینده، و تکمیل‌شده برای سوابق قبلی است.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-search-filter"]', {
		title: 'جستجو و فیلتر',
		description: 'در درخواست‌های دریافتی می‌توانید با نام، سرویس یا تلفن جستجو کنید و رزروها را بر اساس متخصص فیلتر کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-list"]', {
		title: 'لیست رزروها',
		description: 'کارت‌های رزرو اطلاعات مشتری، خدمت، متخصص، زمان مراجعه و مبلغ را نشان می‌دهند.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="request-actions"]', {
		title: 'تایید، رد یا مشاهده جزئیات',
		description: 'در تب در انتظار تایید می‌توانید نوبت مشتری را تایید یا رد کنید. در تب‌های دیگر دکمه مشاهده جزئیات نمایش داده می‌شود.',
		side: 'top',
		align: 'center',
	}),
];

const getSendRequestsSteps = (): DriveStep[] => [
	withElement('[data-business-tour="request-status-tabs"]', {
		title: 'تب‌های درخواست ارسالی',
		description: 'وضعیت نوبت‌هایی که شما ثبت کرده‌اید در سه تب در انتظار تایید، پیش رو و تکمیل‌شده دسته‌بندی می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="send-request-summary"]', {
		title: 'راهنمای درخواست‌های ارسالی',
		description: 'این کارت توضیح می‌دهد در کدام وضعیت هستید و با جستجو می‌توانید نوبت مورد نظر را سریع پیدا کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="request-list"]', {
		title: 'لیست نوبت‌های ارسالی',
		description: 'کارت‌ها اطلاعات مشتری، خدمت، زمان مراجعه و وضعیت هر نوبت ارسالی را نشان می‌دهند.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="request-actions"]', {
		title: 'جزئیات نوبت',
		description: 'از دکمه جزئیات می‌توانید اطلاعات کامل رزرو، خدمات و وضعیت آن را بررسی کنید.',
		side: 'top',
		align: 'center',
	}),
];

const getBookAppointmentSteps = (): DriveStep[] => [
	withElement('[data-business-tour="book-customer"]', {
		title: 'اطلاعات مشتری',
		description: 'نام و شماره همراه مشتری را وارد کنید یا مشتری را از لیست مشتریان انتخاب کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="book-service-artist"]', {
		title: 'انتخاب متخصص و خدمات',
		description: 'ابتدا متخصص را انتخاب کنید تا خدمات قابل انجام او نمایش داده شود، سپس یک یا چند خدمت را برای نوبت انتخاب کنید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="book-date-time"]', {
		title: 'تاریخ و ساعت مراجعه',
		description: 'زمان مراجعه را انتخاب کنید. اگر زمان انتخاب شده با شیفت کاری متخصص سازگار نباشد، پیام خطا نمایش داده می‌شود.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="book-sms-toggle"]', {
		title: 'ارسال پیامک یادآوری',
		description: 'با روشن بودن این گزینه، پیامک یادآوری نوبت برای مشتری ارسال می‌شود.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="book-submit"]', {
		title: 'ثبت رزرو وقت',
		description: 'بعد از تکمیل اطلاعات، رزرو ثبت می‌شود و در لیست درخواست‌های ارسالی/پیش رو قابل پیگیری است.',
		side: 'top',
		align: 'center',
	}),
];

const getCustomerClubSteps = (): DriveStep[] => [
	withElement('[data-business-tour="customer-club-header"]', {
		title: 'باشگاه مشتریان',
		description: 'این صفحه برای شناخت رفتار مشتریان، بررسی بازگشت‌ها و برنامه‌ریزی اقدام‌های نگهداشت مشتری استفاده می‌شود.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-metrics"]', {
		title: 'شاخص‌های اصلی مشتریان',
		description: 'تعداد کل مشتریان، مشتریان بازگشتی، نرخ بازگشت، VIPها و درآمد مشتریان بازگشتی را در یک نگاه می‌بینید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-at-risk"]', {
		title: 'مشتریان در آستانه ریزش',
		description: 'مشتریانی که مدت بیشتری از آخرین مراجعه‌شان گذشته اینجا نمایش داده می‌شوند تا بتوانید برای بازگرداندن آن‌ها پیام یا کمپین بفرستید.',
		side: 'right',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-return-trend"]', {
		title: 'نمودار بازگشت مشتریان',
		description: 'روند بازگشت مشتریان را برای بازه‌های زمانی مختلف بررسی کنید و اثر کمپین‌ها یا تغییرات خدمات را بسنجید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-return-sources"]', {
		title: 'منابع بازگشت',
		description: 'این بخش نشان می‌دهد مشتریان از چه مسیرهایی برگشته‌اند؛ مثلا رزرو مجدد، کمپین پیامکی یا پیشنهاد ویژه.',
		side: 'left',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-campaigns"]', {
		title: 'کمپین‌های فعال',
		description: 'عملکرد کمپین‌ها، تعداد ارسال، نرخ بازگشت و درآمد حاصل را اینجا مقایسه می‌کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-activities"]', {
		title: 'فعالیت‌های اخیر مشتریان',
		description: 'آخرین تعاملات مشتریان مثل رزرو، VIP شدن یا واکنش به کمپین‌ها در این لیست قابل پیگیری است.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="customer-club-actions"]', {
		title: 'اقدام‌های سریع نگهداشت',
		description: 'برای ارسال کمپین پیامکی، ساخت پیشنهاد ویژه یا تنظیم یادآوری خودکار از این دکمه‌ها استفاده کنید.',
		side: 'top',
		align: 'center',
	}),
];

const getGuideSteps = (): DriveStep[] => [
	withElement('[data-business-tour="guide-hero"]', {
		title: 'مرکز آموزش نیوتی',
		description: 'از این صفحه می‌توانید آموزش تمام بخش‌های مهم پنل را پیدا کنید و هر زمان خواستید دوباره آن‌ها را ببینید.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="guide-shortcuts"]', {
		title: 'میان‌برهای شروع سریع',
		description: 'آموزش‌های پرکاربرد مثل ثبت خدمت، ثبت متخصص و فعال‌سازی رزرو آنلاین همیشه بالای صفحه در دسترس هستند.',
		side: 'bottom',
		align: 'center',
	}),
	withElement('[data-business-tour="guide-library"]', {
		title: 'همه آموزش‌ها',
		description: 'آموزش‌ها را جست‌وجو یا بر اساس راه‌اندازی، مدیریت روزانه و رشد کسب‌وکار دسته‌بندی کنید.',
		side: 'top',
		align: 'center',
	}),
	withElement('[data-business-tour="guide-support"]', {
		title: 'کمک بیشتر',
		description: 'اگر پاسخ سؤال‌تان را در آموزش‌ها پیدا نکردید، از این بخش مستقیماً با پشتیبانی در ارتباط باشید.',
		side: 'top',
		align: 'center',
	}),
];

const getPageSteps = (routeName = getRouteName()): DriveStep[] => {
	const pageSteps: Partial<Record<string, () => DriveStep[]>> = {
		Index: getDashboardSteps,
		DashboardSetting: getSettingSteps,
		DashboardArtists: getArtistsSteps,
		DashboardCreateArtist: getCreateArtistSteps,
		DashboardServices: getServicesSteps,
		DashboardServiceArtistAssignments: getAssignmentsSteps,
		DashboardSendSms: getSendSmsSteps,
		DashboardWallet: getWalletSteps,
		DashboardFinancial: getFinancialSteps,
		DashboardRequests: getRequestsHomeSteps,
		DashboardReceiveRequests: getReceiveRequestsSteps,
		DashboardSendRequests: getSendRequestsSteps,
		BookAppointment: getBookAppointmentSteps,
		DashboardCustomerClub: getCustomerClubSteps,
		BusinessGuide: getGuideSteps,
	};

	return pageSteps[routeName]?.() ?? [];
};

type StartTourOptions = {
	auto?: boolean;
	routeName?: string;
	directTourId?: string;
};

const DIRECT_TOUR_SELECTORS: Record<string, string[]> = {
	'online-reservation': ['setting-online-reservation'],
};

const startTour = async (options: StartTourOptions = {}) => {
	await nextTick();

	const routeName = options.routeName || getRouteName();
	if (options.auto) {
		if (!shouldAutoStartRoute(routeName) || hasAutoTourShown(routeName) || routeName !== getRouteName()) {
			return false;
		}
	}

	if (activeTour?.isActive()) return false;
	activeTour = null;

	const navigationSteps = getNavigationSteps().filter(resolveElement);
	let pageSteps = getPageSteps(routeName).filter(resolveElement);
	if (options.directTourId && DIRECT_TOUR_SELECTORS[options.directTourId]) {
		const selectors = DIRECT_TOUR_SELECTORS[options.directTourId];
		pageSteps = pageSteps.filter((step) => selectors.includes(getStepTourTarget(step)));
	}
	if (options.auto && pageSteps.length === 0) return false;

	const shouldIncludeNavigationSteps = !options.directTourId && getNavigationTourCount() < 1 && navigationSteps.length > 0;
	const steps = [...(shouldIncludeNavigationSteps ? navigationSteps : []), ...pageSteps];
	let isNavigationCountTracked = false;

	const trackNavigationShown: NonNullable<DriveStep['onHighlighted']> = (_element, _step, opts) => {
		if (!shouldIncludeNavigationSteps || isNavigationCountTracked) return;
		if (Number(opts.state.activeIndex ?? 0) < navigationSteps.length - 1) return;

		incrementNavigationTourCount();
		isNavigationCountTracked = true;
	};

	const handleHighlighted: NonNullable<DriveStep['onHighlighted']> = (element, step, opts) => {
		refreshAfterScroll(element, step, opts);
		trackNavigationShown(element, step, opts);
	};

	if (!steps.length) return false;

	const tour = driver({
		steps,
		animate: true,
		smoothScroll: true,
		showProgress: true,
		allowKeyboardControl: true,
		overlayColor: '#111827',
		overlayOpacity: 0.58,
		stagePadding: 8,
		stageRadius: 14,
		popoverClass: 'business-driver-popover',
		onHighlightStarted: handleHighlightStarted,
		onHighlighted: handleHighlighted,
		onDestroyed: () => {
			activeTour = null;
		},
		onCloseClick: () => {
			if (options.auto) markAutoTourShown(routeName);
			tour.destroy();
		},
		onPopoverRender: renderPopoverProgress,
		progressText: '{{current}} از {{total}}',
		nextBtnText: 'بعدی',
		prevBtnText: 'قبلی',
		doneBtnText: 'تمام',
		showButtons: ['previous', 'next', 'close'],
	});

	activeTour = tour;
	if (options.auto) markAutoTourShown(routeName);
	tour.drive();
	return true;
};

const clearAutoTourTimeout = () => {
	if (typeof window === 'undefined' || autoTourTimeout === undefined) return;
	window.clearTimeout(autoTourTimeout);
	autoTourTimeout = undefined;
};

const scheduleAutoTour = () => {
	if (typeof window === 'undefined') return;

	clearAutoTourTimeout();

	const routeName = getRouteName();
	if (!isBusinessUser.value || !shouldAutoStartRoute(routeName) || hasAutoTourShown(routeName)) return;

	let attempt = 0;
	const tryStart = async () => {
		if (!isBusinessUser.value || routeName !== getRouteName() || hasAutoTourShown(routeName)) return;

		const started = await startTour({ auto: true, routeName });
		if (started || attempt >= 8) return;

		attempt += 1;
		autoTourTimeout = window.setTimeout(tryStart, 500);
	};

	autoTourTimeout = window.setTimeout(tryStart, 700);
};

const scheduleDirectTour = () => {
	if (typeof window === 'undefined' || !isBusinessUser.value) return;
	const directTourId = String(route.query.tour || '');
	if (!directTourId) return;
	const directKey = `${route.fullPath}:${String(route.query.run || '')}`;
	if (processedDirectTour === directKey) return;

	clearAutoTourTimeout();
	let attempt = 0;
	const tryStart = async () => {
		if (directKey !== `${route.fullPath}:${String(route.query.run || '')}`) return;
		const started = await startTour({ routeName: getRouteName(), directTourId });
		if (started) {
			processedDirectTour = directKey;
			return;
		}
		if (attempt >= 8) return;
		attempt += 1;
		autoTourTimeout = window.setTimeout(tryStart, 400);
	};
	autoTourTimeout = window.setTimeout(tryStart, 350);
};

onMounted(() => {
	if (route.query.tour) scheduleDirectTour();
	else scheduleAutoTour();
});

watch(
	() => [route.name, route.query.tour, route.query.run, isBusinessUser.value],
	() => {
		if (route.query.tour) scheduleDirectTour();
		else scheduleAutoTour();
	},
);

onBeforeUnmount(() => {
	clearAutoTourTimeout();
	if (activeTour?.isActive()) activeTour.destroy();
	activeTour = null;
});
</script>

<template>
	<button
		v-if="isBusinessUser"
		type="button"
		class="fixed bottom-[5.2rem] left-3 z-[60] inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#4848fe] text-white shadow-[0_12px_24px_rgba(72,72,254,0.28)] ring-1 ring-white/60 transition hover:bg-[#3d3de8] sm:bottom-6 sm:left-6 sm:h-12 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm sm:font-extrabold sm:shadow-[0_16px_32px_rgba(72,72,254,0.28)]"
		aria-label="شروع آموزش بخش های کسب و کار"
		@click="route.name === 'BusinessGuide' ? startTour({ directTourId: 'guide-overview' }) : router.push('/dashboard/guide')"
	>
		<HelpCircle :size="20" stroke-width="2.4" />
		<span class="hidden sm:inline">{{ route.name === 'BusinessGuide' ? 'معرفی این صفحه' : 'مرکز آموزش' }}</span>
		<PlayCircle :size="18" stroke-width="2.4" class="hidden sm:inline" />
	</button>
</template>

<style>
.business-driver-popover {
	direction: rtl;
	max-width: min(360px, calc(100vw - 32px));
	border-radius: 16px;
	font-family: 'IRANYekan', sans-serif !important;
	text-align: right;
}

.business-driver-popover,
.business-driver-popover * {
	font-family: 'IRANYekan', sans-serif !important;
}

.business-driver-popover .driver-popover-title {
	font-size: 16px;
	font-weight: 900;
	line-height: 1.8;
}

.business-driver-popover .driver-popover-description {
	color: #4b5563;
	font-size: 13px;
	font-weight: 700;
	line-height: 2;
}

.business-driver-popover .driver-popover-footer {
	direction: ltr;
	margin-top: 18px;
}

.business-driver-popover .driver-popover-progress-text {
	color: #6b7280;
	direction: rtl;
	font-size: 12px;
	font-weight: 800;
	text-align: right;
	unicode-bidi: isolate;
	white-space: nowrap;
}

.business-driver-popover .driver-popover-prev-btn,
.business-driver-popover .driver-popover-next-btn,
.business-driver-popover .driver-popover-footer button {
	border: 0;
	border-radius: 10px;
	padding: 8px 14px;
	font-family: 'IRANYekan', sans-serif !important;
	font-size: 12px;
	font-weight: 900;
	text-shadow: none;
}

.business-driver-popover .driver-popover-next-btn,
.business-driver-popover .driver-popover-next-btn:hover,
.business-driver-popover .driver-popover-next-btn:focus {
	background: #4848fe !important;
	color: #ffffff !important;
}

.business-driver-popover .driver-popover-prev-btn,
.business-driver-popover .driver-popover-prev-btn:hover,
.business-driver-popover .driver-popover-prev-btn:focus {
	background: #eef2ff !important;
	color: #3730a3 !important;
}

.business-driver-popover .driver-popover-close-btn,
.business-driver-popover .driver-popover-close-btn:hover,
.business-driver-popover .driver-popover-close-btn:focus {
	background: transparent !important;
	color: #111827 !important;
	text-shadow: none !important;
}
</style>
